<template>
    <BaseLayout
        :title="pageTitle"
        overflowColor="bg-background-overflow-color"
        borderClass="border-r border-sidebar-border"
    >
        <Announcement :show="shouldShowAnnouncement" />
        <div class="flex">
            <div
                id="sidebar"
                :class="[
                    showSidebar ? '-translate-x-0' : '-translate-x-full',
                    'w-full lg:w-[--sidebar-width] flex-shrink-0 fixed bg-sidebar lg:transform-none z-mobilemenu transition-transform lg:transition-none duration-500 ease-in-out text-navbar-mobile-link lg:text-navbar-link border-x border-sidebar-border border-l h-screen',
                ]"
            >
                <div
                    :class="[
                        shouldShowAnnouncement
                            ? 'h-[calc(100dvh-theme(height.10))]'
                            : 'h-dvh',
                        'flex flex-col',
                    ]"
                >
                    <MainSidebarContent
                        @toggleExpandedMenuItem="toggleExpandedMenuItem"
                        @linkClicked="onLinkClicked"
                        @toggleAdminMenuOpen="
                            (open = null) =>
                                (adminMenuOpen =
                                    open != null ? open : !adminMenuOpen)
                        "
                        @toggleProfileMenu="
                            (open = null) =>
                                (profileMenuOpen =
                                    open != null ? open : !profileMenuOpen)
                        "
                        :expandedMenuItem="expandedMenuItem"
                        :adminMenuOpen="adminMenuOpen"
                        :profileMenuOpen="profileMenuOpen"
                        :profileItems="profileItems"
                        :adminLinks="adminLinks"
                        :contentLinks="contentLinks"
                    />

                    <button
                        @click="showSidebar = !showSidebar"
                        :class="[
                            showSidebar
                                ? 'right-3 top-4 bg-sidebar'
                                : '-right-14 top-5',
                            'block lg:hidden fixed rounded-full p-2 transition-all duration-500 ease-in-out ',
                        ]"
                    >
                        <transition name="fade" mode="out-in">
                            <PhX v-if="showSidebar" class="w-8 fill-black" />
                            <PhList v-else class="w-8 fill-black" />
                        </transition>
                    </button>
                </div>
            </div>
            <Navbar
                id="navbar"
                class="lg:w-[calc(min(var(--max-site-width),100%)-var(--sidebar-width))]"
            />
            <div
                :class="[
                    'w-full lg:w-[calc(100%-var(--sidebar-width))] lg:ml-[--sidebar-width] bg-background',
                    {
                        'min-h-[calc(100dvh-var(--announcement-height))]':
                            shouldShowAnnouncement,
                        'min-h-screen': !shouldShowAnnouncement,
                    },
                ]"
            >
                <div id="content" :class="[contentClasses]">
                    <slot />
                    <!-- <transition name="page" mode="in-out">
                                    <slot v-if="animate" />
                                </transition> -->
                </div>
            </div>
        </div>
    </BaseLayout>
</template>

<script>
import BaseLayout from '@/Layouts/BaseLayout.vue'
import MainSidebarContent from '@/Layouts/MainLayout/MainSidebarContent.vue'
import Navbar from '@/Layouts/MainLayout/Navbar.vue'
import Announcement from '@/Components/Other/Announcement.vue'
import { PhList, PhX } from '@phosphor-icons/vue'
import { mapActions, mapState } from 'pinia'
import { screenStore } from '@/Store/screen'
import { throttle } from '@/Tools/utilities.js'
import { router } from '@inertiajs/vue3'

export default {
    components: {
        BaseLayout,
        Announcement,
        MainSidebarContent,
        Navbar,
        PhX,
        PhList,
    },

    props: {
        pageTitle: {
            type: String,
            default: null,
        },
        useLayoutPadding: {
            type: Boolean,
            default: true,
        },
        layoutFixedFullScreen: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        expandedMenuItem: null,
        adminMenuOpen: false,
        profileMenuOpen: false,
        animate: false,
        showSidebar: false,
        throttledResize: null,
        removeOnNavigateListener: null,
    }),

    created() {
        this.throttledResize = throttle(
            () => {
                this.updateScreenSize()
            },
            300,
            true
        )
        this.updateScreenSize()

        window.addEventListener('resize', this.throttledResize)

        this.$page.props.sidebar.links.every((link) => {
            if (this.$page.url === link.url) {
                return false
            }
            if (link.links) {
                link.links.forEach((child) => {
                    if (this.$page.url === child.url) {
                        this.expandedMenuItem = link.title
                        return false
                    }
                })
            }
            return true
        })
    },

    beforeUnmount() {
        if (this.throttledResize) {
            window.removeEventListener('resize', this.throttledResize)
        }
    },

    mounted() {
        this.animate = true

        this.setMenuOpenStates()

        this.removeOnNavigateListener = router.on(
            'navigate',
            this.handleNavigation
        )
    },

    beforeUnmount() {
        if (this.removeOnNavigateListener) {
            this.removeOnNavigateListener()
        }
    },

    watch: {
        showSidebar() {
            if (
                this.showSidebar &&
                [null, 'sm', 'md'].includes(this.currentBreakpoint)
            ) {
                document.body.style.overflow = 'hidden'
            } else {
                document.body.style.overflow = 'auto'
            }
        },
    },

    computed: {
        ...mapState(screenStore, ['currentBreakpoint']),

        profileItems() {
            if (this.$page.props?.auth?.user) {
                return [
                    {
                        text: this.$lang.get('My Profile'),
                        routeName: 'user.show',
                        route: this.$localizedRoute(
                            'user.show',
                            this.$page.props?.auth?.user
                        ),
                        active: this.isCurrentUserActive(),
                    },
                    {
                        text: this.$lang.get('Invitations & requests'),
                        route: route('profile.invitationsAndRequests'),
                        active: route().current(
                            'profile.invitationsAndRequests'
                        ),
                    },
                    {
                        text: this.$lang.get('My Messages'),
                        route: route('chats.index'),
                        active: route().current('chats.index'),
                    },
                ]
            }
            return []
        },

        adminLinks() {
            return this.$page.props.sidebar.admin_links.map((link) => ({
                ...link,
                active: this.profileLinkIsActive
                    ? false
                    : this.isAnyModelRouteActive(link.routeName),
            }))
        },

        contentLinks() {
            return this.$page.props.sidebar.links.map((link) => ({
                ...link,
                active: this.isAnyModelRouteActive(link.routeName),
            }))
        },

        adminLinkIsActive() {
            return this.adminLinks.some((link) => link.active)
        },

        profileLinkIsActive() {
            return this.profileItems.some((link) => link.active)
        },

        /**
         * We check if any of the links that have a url property is active
         */
        isAnyUrlLinkActive() {
            return this.$page.props.sidebar.links.some((link) => {
                if (link.url) {
                    return this.$page.url == link.url
                } else if (Array.isArray(link.links)) {
                    return link.links.some(
                        (child) => this.$page.url == child.url
                    )
                }
                return false
            })
        },

        shouldShowAnnouncement() {
            return !!this.$page.props.announcement
        },

        contentClasses() {
            let classes = [
                {
                    'py-3 px-3 lg:py-5 lg:px-5': this.useLayoutPadding,
                },
                'mt-[--navbar-height] flex flex-col',
            ]

            if (this.layoutFixedFullScreen) {
                classes.push({
                    'h-[calc(100dvh-var(--announcement-height)-var(--navbar-height))]':
                        this.shouldShowAnnouncement,
                    'h-[calc(100dvh-var(--navbar-height))]':
                        !this.shouldShowAnnouncement,
                })
            } else {
                classes.push({
                    'min-h-[calc(100dvh-var(--announcement-height)-var(--navbar-height))]':
                        this.shouldShowAnnouncement,
                    'min-h-[calc(100dvh--var(--navbar-height))]':
                        !this.shouldShowAnnouncement,
                })
            }

            return classes
        },
    },

    methods: {
        ...mapActions(screenStore, ['updateScreenSize']),

        toggleExpandedMenuItem(title) {
            this.expandedMenuItem =
                this.expandedMenuItem === title ? null : title
        },

        onLinkClicked(link) {
            // If the link has a url, we try to find if that url has a parent (menuItem) so that we can set the expandedMenuItem to that
            let closeExpandedMenuItem = true
            if (link.url) {
                // Find the parent menu item
                const parent = this.$page.props.sidebar.links.find((item) =>
                    item.links?.some((child) => child.url === link.url)
                )
                if (parent) {
                    this.expandedMenuItem = parent.title
                    closeExpandedMenuItem = false
                }
            }
            this.closeSidebar(closeExpandedMenuItem)
        },

        closeSidebar(closeExpandedMenuItem = true) {
            this.showSidebar = false
            if (closeExpandedMenuItem) {
                this.expandedMenuItem = null
            }
            setTimeout(() => {
                if (!this.adminLinkIsActive) {
                    this.adminMenuOpen = false
                }
                if (!this.profileLinkIsActive) {
                    this.profileMenuOpen = false
                }
            }, 400)
        },

        /**
         * If the routename exists and it contains dot-notation we extract the model name and pass it to isAnyModelRouteActive
         * to figure out if any route with that model is active
         *
         * If a route is active we have to check that another link with the url property does not have the same url
         * If it does then this route is not active, we dont want two items to be active at the same time
         */
        isAnyModelRouteActive(
            routeName,
            routes = ['create', 'edit', 'show', 'index']
        ) {
            if (!this.isAnyUrlLinkActive && routeName) {
                if (route().has(routeName)) {
                    return route().current(
                        routeName.includes('.')
                            ? routeName.split('.')[0] + '*'
                            : routeName
                    )
                } else {
                    let modelName = routeName
                    if (routeName.includes('.')) {
                        modelName = routeName.split('.')[0]
                    }
                    // if(route().check(routeName)) {

                    return routes.some((route) =>
                        this.$isCurrentLocalizedRoute(modelName + '.' + route)
                    )
                }
            }
            return false
        },

        /**
         * Check if the route user.show or user.edit is active,
         * Then check if that user shown/edited is the logged in user
         * In that case return true
         */
        isCurrentUserActive() {
            let userRouteActive = this.isAnyModelRouteActive('user.show', [
                'show',
                'edit',
            ])

            if (userRouteActive) {
                return this.$page.props.isMe
            }
            return false
        },

        handleNavigation(event) {
            this.setMenuOpenStates()
        },

        setMenuOpenStates() {
            if (this.profileLinkIsActive) {
                this.profileMenuOpen = true
            } else if (this.adminLinkIsActive) {
                this.adminMenuOpen = true
            }
        },
    },
}
</script>

<style scoped>
.page-enter-active,
.page-leave-active {
    transition: opacity 450ms;
}

.page-enter-from,
.page-leave-to {
    opacity: 0;
}
</style>
